/**
 * Client-side mirror of the backend G702/G703 math (integer cents) so the pay
 * application editor can show a live footer while typing. The backend
 * recompute on save/submit is the source of truth — this exists only for
 * immediate feedback and must follow the same rules:
 * per line G = D + E + F, retainage = workPct·(D+E) + storedPct·F, and the
 * G702 chain down to current payment due.
 */

export interface LiveSovLine {
  id: string;
  scheduledValue: number; // dollars
  isChangeOrder: boolean;
  previousCompleted: number; // dollars (D)
}

export interface LiveEntry {
  workThisPeriod: number; // dollars (E)
  storedMaterials: number; // dollars (F)
}

export interface LiveTotals {
  originalContractSum: number;
  netChangeOrders: number;
  contractSumToDate: number;
  totalCompletedAndStored: number;
  retainageWork: number;
  retainageStored: number;
  totalEarnedLessRetainage: number;
  previousCertificates: number;
  currentPaymentDue: number;
  balanceToFinish: number;
  lineErrors: Map<string, string>;
}

const cents = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 100);
const pct = (c: number, p: number) => Math.round((c * p) / 100);

export function computeLiveTotals(
  sovLines: LiveSovLine[],
  entries: Map<string, LiveEntry>,
  retainageWorkPct: number,
  retainageStoredPct: number,
  previousCertificates: number,
): LiveTotals {
  let original = 0;
  let changeOrders = 0;
  let completedAndStored = 0;
  let cumulativeWork = 0;
  let storedBalance = 0;
  const lineErrors = new Map<string, string>();

  for (const line of sovLines) {
    const entry = entries.get(line.id) ?? { workThisPeriod: 0, storedMaterials: 0 };
    const sched = cents(line.scheduledValue);
    const prev = cents(line.previousCompleted);
    const work = cents(entry.workThisPeriod);
    const stored = cents(entry.storedMaterials);
    const total = prev + work + stored;
    if (total > sched) {
      lineErrors.set(line.id, 'Exceeds scheduled value');
    }
    if (line.isChangeOrder) changeOrders += sched;
    else original += sched;
    completedAndStored += total;
    cumulativeWork += prev + work;
    storedBalance += stored;
  }

  const contractSum = original + changeOrders;
  const retWork = pct(cumulativeWork, retainageWorkPct);
  const retStored = pct(storedBalance, retainageStoredPct);
  const earned = completedAndStored - retWork - retStored;
  const prevCerts = cents(previousCertificates);

  const d = (c: number) => c / 100;
  return {
    originalContractSum: d(original),
    netChangeOrders: d(changeOrders),
    contractSumToDate: d(contractSum),
    totalCompletedAndStored: d(completedAndStored),
    retainageWork: d(retWork),
    retainageStored: d(retStored),
    totalEarnedLessRetainage: d(earned),
    previousCertificates: d(prevCerts),
    currentPaymentDue: d(earned - prevCerts),
    balanceToFinish: d(contractSum - earned),
    lineErrors,
  };
}

export function fmtMoney(value: number | string | null | undefined): string {
  const n = Number(value ?? 0);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

/** How the two editable G703 columns are keyed in. */
export type EntryMode = 'amount' | 'percent';

/** Percent text as typed, kept alongside the dollar drafts it derives.
 *  Typing "6" then "0" must not fight a round-trip through dollars, so the
 *  text the operator sees is its own state; dollars stay the source of truth
 *  for the live G702 math and for save. */
export interface PercentDraft {
  work: string;
  stored: string;
}

/** Dollars a percentage of the line's scheduled value represents.
 *
 * Rounds through integer cents to match the backend's arithmetic exactly. This
 * is the ONLY direction a percentage is allowed to drive: a percentage names a
 * new intent, it never re-derives a dollar amount already on the books. The
 * percent text is rounded for display, so round-tripping an existing amount
 * through it silently moves money — on a $186,000 line, 0.01% is $18.60.
 */
export function percentOfScheduled(pct: number, scheduled: number): number {
  return Math.round((Math.round(scheduled * 100) * pct) / 100) / 100;
}

/** Split a line's cumulative complete-and-stored target into its G703 columns.
 *
 * G703 reads `total = previousCompleted + workThisPeriod + storedMaterials`,
 * and the `%` column is that total over the scheduled value. So the cumulative
 * percentage on screen is only honest if stored lives INSIDE the target rather
 * than on top of it: stored is clamped into what the target leaves after prior
 * applications, and work takes the remainder.
 *
 * Without the clamp, asking for 20% complete with 30% stored floors work at 0
 * and bills the line at 30% while the cumulative column still reads 20%.
 *
 * All integer cents; the target is floored at what prior applications already
 * completed, since this period cannot un-bill them.
 */
export function splitCumulativeTarget(
  target: number,
  previous: number,
  stored: number,
): { work: number; stored: number } {
  const targetCents = Math.max(Math.round(target * 100), Math.round(previous * 100));
  const remainingCents = targetCents - Math.round(previous * 100);
  const storedCents = Math.min(Math.max(Math.round(stored * 100), 0), remainingCents);
  return {
    work: (remainingCents - storedCents) / 100,
    stored: storedCents / 100,
  };
}

/** Read a dollar amount back as the percentage of the line it represents. */
export function dollarsToPercentDraft(amount: number, scheduled: number): string {
  if (scheduled <= 0 || amount <= 0) return '';
  const pct = (amount / scheduled) * 100;
  // Two decimals round-trips a cent on a seven-figure line.
  return String(Math.round(pct * 100) / 100);
}

/** A Schedule of Values line's progress: billed to date over its scheduled
 *  value, the same ratio the G703 prints in its `%` column.
 *
 *  Returns display text, not a number, because "no scheduled value yet" is a
 *  real state on an in-progress schedule and has no percentage — a line being
 *  typed reads `—`, never `0%` or `Infinity%`. A decimal place is kept only
 *  when it carries information, so a finished line reads `100%` rather than
 *  `100.0%`.
 */
export function percentBilled(billed: number, scheduledValue: string | number): string {
  const scheduled = Number(scheduledValue);
  if (!Number.isFinite(scheduled) || scheduled <= 0) return '—';
  const pct = Math.round((billed / scheduled) * 1000) / 10;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}
